/**
 * Hook that mirrors CustomerHOC's data-loading behavior for a given
 * customer id: seeds the Redux customer context and triggers the same
 * entity fetches (customer entity, feature associations, customer types,
 * features) that the legacy HOC performed.
 *
 * Intended for use in CustomerLayout and any view that owns its own
 * customer context rather than relying on the HOC chain.
 *
 * Contains:
 * - useCustomer — fetches and returns the selected customer + loading state
 */
import { isAfter, parseISO, subMinutes } from 'date-fns';
// eslint-disable-next-line no-restricted-imports -- pre-existing Immutable usage; migrate off when touched
import { Map } from 'immutable';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchCustomer } from '@sophocles/entities/Customer';
import { setCustomer } from '@sophocles/features/auth/authActions';
import { getSelectedCustomerSelector } from '@sophocles/features/auth/authSelectors';
import { loadCustomerTypes } from '@sophocles/features/customers/api';
import { loadCustomerFeatureAssociations, loadFeatures } from '@sophocles/features/featureFlags/api';
import { setEntityData } from '@sophocles/features/resources/resourceHandlerActions';
import { getEntityDataSelector } from '@sophocles/features/resources/resourceHandlerSelectors';

/**
 * Fetch customer data and seed the customer Redux context.
 *
 * @param {string | number} customerId  URL param value (cust_id)
 * @returns {{ customer: import('immutable').Map, loading: boolean, error: boolean, reload: () => void }}
 */
export default function useCustomer(customerId) {
  const dispatch = useDispatch();
  const customer = useSelector(getSelectedCustomerSelector);
  const entityData = useSelector(getEntityDataSelector);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Bumped by reload() to re-run the seed effect after a failed fetch.
  const [reloadToken, setReloadToken] = useState(0);
  const reload = useCallback(() => setReloadToken(token => token + 1), []);

  // Track which customer id we've already loaded so effect deps don't
  // retrigger unnecessarily on re-renders.
  const lastLoadedRef = useRef(null);

  // Keep a ref to the latest entityData so the effect can read the current
  // cache state without listing entityData as a dep (which would cause the
  // effect to re-run on every store update — mirroring the HOC's
  // componentDidUpdate-on-param-change behavior).
  const entityDataRef = useRef(entityData);
  entityDataRef.current = entityData;

  useEffect(() => {
    if (!customerId) return;

    dispatch(setCustomer(customerId));

    const currentEntityData = entityDataRef.current;
    const fiveMinutesAgo = subMinutes(new Date(), 5);
    const meta = currentEntityData.get('Customer', Map());
    const isStale =
      meta.isEmpty() ||
      meta.get('id', 0) != customerId ||
      isAfter(fiveMinutesAgo, parseISO(meta.get('timestamp', new Date().toISOString())));

    if (isStale) {
      setLoading(true);
      setError(false);
      dispatch(fetchCustomer(customerId))
        .then(() => dispatch(setEntityData('Customer', { id: customerId })))
        .then(() => setLoading(false))
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    }

    // Feature associations are per-customer — reload when the id changes.
    if (lastLoadedRef.current !== customerId) {
      lastLoadedRef.current = customerId;
      dispatch(loadCustomerFeatureAssociations(customerId));
    }

    // Reference lists: only fetch once per session (entityDataRef acts as cache).
    if (!currentEntityData.has('CustomerTypes')) {
      dispatch(loadCustomerTypes());
    }
    if (!currentEntityData.has('Features')) {
      dispatch(loadFeatures());
    }
  }, [customerId, dispatch, reloadToken]);

  return { customer, loading, error, reload };
}
